import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { ErrorMessage, ModalOpenButton, useScreenWidth } from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Button, IconButton, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, Route, Routes, useMatch, useParams } from "react-router-dom";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import {
  RestrictedByOrganisationPermission,
  RestrictedByOrganisationPermissionWithDebug,
} from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { LayoutView } from "../../../commons/button/ToggleLayoutViewButtonGroup.js";
import { usePageWidth } from "../../../commons/hooks/usePageWidth.js";
import { PageTopbarItem } from "../../../commons/layout/Page.js";
import { PageNotFound } from "../../../commons/PageNotFound.js";
import { SelectInfoMessageItem } from "../../../features/doc-items/items/SelectInfoMessageItem.js";
import { QuoteTemplateToggleApplyOverridesButton } from "../../../features/templates/quote/buttons/QuoteTemplateToggleApplyOverridesButton.js";
import { TemplatesQuoteTemplateVersionSettingsModal } from "../../../features/templates/quote/TemplatesQuoteTemplateSettingsModal.js";
import { TemplatesQuoteTemplateSubHeader } from "../../../features/templates/quote/TemplatesQuoteTemplateSubHeader.js";
import { fromVersionNumberParamToVersionNumberInt } from "../../../utils.js";
import { TemplateQuoteTemplateType } from "./TemplateQuoteTemplateType.js";
import { TemplateQuoteTemplateTypes } from "./TemplateQuoteTemplateTypes.js";
import { TemplateQuoteVersion } from "./TemplateQuoteVersion.js";
import { TemplateQuoteVersionItem } from "./TemplateQuoteVersionItem.js";
import { useTemplateQuoteVersionRoutesQuery } from "./TemplateQuoteVersionRoutes.generated.js";
import { TemplateQuoteThreeDVersion } from "./TemplateQuoteVersionThreeD.js";
import { TemplateQuoteVersionTroubleshoot } from "./TemplateQuoteVersionTroubleshoot.js";

const REFETCH_QUERIES_LIST = [namedOperations.Query.TemplateQuotes];
const REFETCH_QUERIES_PREVIEW = [
  namedOperations.Query.TemplateQuoteEditPreview,
];

type PageSubmenuItem = PageTopbarItem;

interface Props {
  submenuItems: PageSubmenuItem[];
}

export const TemplateQuoteVersionRoutes = ({ submenuItems }: Props) => {
  const { id, versionNumber: versionNumberFromParams } = useParams();
  if (!id) throw new Error("Template id is missing");

  const viewer = useUserData().currentUser!;

  const { t } = useTranslate(["SidebarItems", "Templates", "Global"]);
  const { isMinTablet } = useScreenWidth();
  const { isMinOneColumnWithPreview, isMinTwoColumnsWithPreview } =
    usePageWidth();

  const versionNumber = fromVersionNumberParamToVersionNumberInt(
    versionNumberFromParams
  );

  const client = useApolloClient();
  const query = useTemplateQuoteVersionRoutesQuery({
    client,
    variables: { id, versionNumber: versionNumber!, applyOverrides: false },
  });

  const doc = getDataOrNull(query.data?.quoteTemplateVersion)?.quoteTemplate;
  const docLatestAvailableVersion = getDataOrNull(
    query.data?.quoteTemplateLatestAvailableVersion
  )?.quoteTemplate;
  const docTitle = doc?.title ?? "";
  const rootItem = doc?.rootItem;

  const pathToDocList = `/templates/documents`;
  const pathToDoc = `${pathToDocList}/${id}`;
  const pathToDocVersion = `${pathToDoc}/${versionNumber}`;

  const breadcrumbs = React.useMemo(
    () => [
      {
        title: t("Templates", { ns: "SidebarItems" }),
        to: pathToDocList,
      },
      { title: docTitle, to: pathToDocVersion },
    ],
    [docTitle, pathToDocVersion, pathToDocList, t]
  );

  const activeView = useActiveView(pathToDocVersion);

  const pageSubHeader =
    doc && rootItem ? (
      <TemplatesQuoteTemplateSubHeader
        docId={doc.id}
        doc={doc}
        rootItem={rootItem}
        rootItemId={rootItem.id}
        pathToDocList={pathToDocList}
        pathToDoc={pathToDoc}
        pathToDocVersion={pathToDocVersion}
        onDuplicateRefetchQueries={REFETCH_QUERIES_LIST}
        onUpdateDataRefetchQueries={REFETCH_QUERIES_PREVIEW}
        activeView={activeView}
        isHeaderVisible={undefined as never}
        setHeaderVisible={undefined as never}
        canEdit={false}
        ToggleApplyOverridesButton={
          doc.owningSystemOrganisationId !== viewer.organisation.id &&
          doc.hasOverridesDefined ? (
            <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
              <QuoteTemplateToggleApplyOverridesButton />
            </RestrictedByOrganisationPermissionWithDebug>
          ) : null
        }
        SettingsModalButton={
          <ModalOpenButton
            Modal={TemplatesQuoteTemplateVersionSettingsModal}
            modalProps={{
              templateId: doc.id,
              versionNumber: versionNumber!,
              pathToDocList,
            }}
          >
            <Tooltip title={t("Info", { ns: "Global" })}>
              <IconButton size="small" color="primary">
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </ModalOpenButton>
        }
      />
    ) : null;

  if (!query.loading && !query.error && !doc) {
    return (
      <ErrorMessage
        message={t("This version of template is not available anymore", {
          ns: "Templates",
        })}
        button={
          docLatestAvailableVersion ? (
            <Button
              component={Link}
              to={`${pathToDoc}/${docLatestAvailableVersion.resolvedAsReadModelVersionNumber}`}
              variant="outlined"
            >
              {t("Go to latest version", { ns: "Templates" })}
            </Button>
          ) : (
            <Button component={Link} to={pathToDocList} variant="outlined">
              {t("Go back to template list", { ns: "Templates" })}
            </Button>
          )
        }
      />
    );
  }

  return (
    <Routes>
      <Route
        path={"/"}
        element={
          doc && rootItem ? (
            <TemplateQuoteVersion
              docId={id}
              pathToDocList={pathToDocList}
              pathToDoc={pathToDoc}
              pathToDocPage={pathToDocVersion}
              submenuItems={submenuItems}
              prefixBreadcrumbs={breadcrumbs}
              pageSubHeader={pageSubHeader}
              hasOverridesDefined={doc.hasOverridesDefined}
            />
          ) : null
        }
      >
        <Route
          path={`items/:itemId`}
          element={
            doc && rootItem ? (
              <TemplateQuoteVersionItem
                docId={id}
                pathToDocList={pathToDocList}
                pathToDoc={pathToDoc}
                pathToDocPage={pathToDocVersion}
                hasOverridesDefined={doc.hasOverridesDefined}
              />
            ) : null
          }
        />
        {isMinTablet &&
          (isMinTwoColumnsWithPreview || !isMinOneColumnWithPreview) && (
            <Route index element={<SelectInfoMessageItem />} />
          )}
      </Route>
      <Route
        path={`3d`}
        element={
          doc && rootItem ? (
            <TemplateQuoteThreeDVersion
              docId={id}
              pathToDoc={pathToDoc}
              pathToDocList={pathToDocList}
              pathToDocVersion={pathToDocVersion}
              submenuItems={submenuItems}
              prefixBreadcrumbs={breadcrumbs}
              pageSubHeader={pageSubHeader}
              hasOverridesDefined={doc.hasOverridesDefined}
            />
          ) : null
        }
      />
      <Route
        path={`troubleshoot`}
        element={
          <RestrictedByOrganisationPermission permission="MANAGE_TEMPLATE">
            <TemplateQuoteVersionTroubleshoot
              docId={id}
              pathToDocList={pathToDocList}
              submenuItems={submenuItems}
              prefixBreadcrumbs={breadcrumbs}
              pageSubHeader={pageSubHeader}
            />
          </RestrictedByOrganisationPermission>
        }
      />
      <Route
        path={`template-types`}
        element={
          <RestrictedByOrganisationPermission permission="MANAGE_TEMPLATE">
            <TemplateQuoteTemplateTypes
              docId={id}
              submenuItems={submenuItems}
              breadcrumbs={breadcrumbs.concat({
                title: t("Used template types", { ns: "Templates" }),
                to: `${pathToDocVersion}/template-types`,
              })}
              pageSubHeader={pageSubHeader}
            />
          </RestrictedByOrganisationPermission>
        }
      />

      <Route
        path={`template-types/:templateTypeId`}
        element={
          <RestrictedByOrganisationPermission permission="MANAGE_TEMPLATE">
            <TemplateQuoteTemplateType
              docId={id}
              submenuItems={submenuItems}
              pageSubHeader={pageSubHeader}
              prefixBreadcrumbs={breadcrumbs.concat({
                title: t("Used template types", { ns: "Templates" }),
                to: `${pathToDocVersion}/template-types`,
              })}
              pathToDocList={pathToDocList}
            />
          </RestrictedByOrganisationPermission>
        }
      />

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

function useActiveView(pathToDoc: string): LayoutView | null {
  const matchIndex = useMatch(`${pathToDoc}/`);
  const matchItem = useMatch(`${pathToDoc}/items/*`);
  const match3d = useMatch(`${pathToDoc}/3d/*`);

  const activeView: LayoutView | null =
    matchIndex || matchItem ? "tree" : match3d ? "3d" : null;

  return activeView;
}
